/**
 *  JEGY-MÓDOSÍTÁS, NEGYEDÉVES ÉS DICSÉRETES JEGY, „NEM ÍRTA MEG” (–) – közös logika a
 *  naplónak, az Értékelések menünek és a szülői nézetnek.
 *
 *  A `tanulo_ertekeles.modja`: 1 évközi, 2 félévi, 3 év végi, 4 I., 6 II., 5 III., 7 IV.
 *  negyedéves. A II. és a IV. negyedév 2026-11-02 óta KÜLÖN értékelés, nem azonos a félévi
 *  és az év végi jeggyel (az intézmények jelezték). A negyedéves jegynek nincs külön
 *  oszlopa: a hónapjában jelenik meg, megjelölve, és az átlagba nem számít – összegzi az
 *  évközi jegyeket, nem újabb jegy.
 *
 *  A módosítás szabálya a kiszolgálóéval azonos (`ertekeles.js` → `ertekFormaSzerint`):
 *  a forma marad, csak az érték, a dátum és a téma változik.
 */
import { AlertController } from '@ionic/angular';

export const modjaKod = (item: any): string => String(item?.modja ?? '').trim();

/** I–IV. negyedéves értékelés. */
export const negyedevesE = (item: any): boolean => ['4', '5', '6', '7'].includes(modjaKod(item));

/** Záró- vagy negyedéves értékelés (a jegyet összegzi, nem évközi). */
export const osszegzoE = (item: any): boolean => ['2', '3', '4', '5', '6', '7'].includes(modjaKod(item));

export const NEGYEDEV_JEL: Record<string, string> = { '4': 'N1', '6': 'N2', '5': 'N3', '7': 'N4' };
export const NEGYEDEV_NEV: Record<string, string> = {
  '4': 'I. negyedéves értékelés', '6': 'II. negyedéves értékelés',
  '5': 'III. negyedéves értékelés', '7': 'IV. negyedéves értékelés',
};

const FOKOZATOK = ['Példás', 'Jó', 'Változó', 'Hanyag', 'Rossz'];

/*  „NEM ÍRTA MEG” (–).
 *
 *  A KRÉTÁ-hoz hasonlóan jegy helyett mínusz annak, aki a dolgozatot nem írta meg. Nem
 *  jegy: az átlagba nem számít, a KRÉTÁ-ba nem megy, és amikor a tanuló pótolta, a
 *  módosítással jegyre írható át. Csak évközi érdemjegynél és százaléknál (ott van
 *  dolgozat). A kiszolgáló szabálya ugyanez (`ertekeles.js` → `nemIrtaJelE`). */
export const NEM_IRTA_JEL = '–';

/** A beírt érték a „nem írta meg” jel-e (a billentyűzettől függően többféle vonal jöhet). */
export const nemIrtaJelE = (v: any): boolean => /^[-–—−]$/.test(String(v ?? '').trim());

/** A rögzített tétel „nem írta meg” jelölés-e. */
export const nemIrtaE = (item: any): boolean => Number(item?.nem_irta) === 1;

/** Az osztályozási módnál (1 osztályzat, 2 százalék) tehető-e „nem írta meg” jel. */
export const nemIrtaModE = (osztalyozasimod: any): boolean => ['1', '2'].includes(String(osztalyozasimod ?? ''));

const ures = (v: any) => v === null || v === undefined || String(v).trim() === '';

/**
 *  A záró- vagy negyedéves érdemjegy / százalék rövid alakja („5D”, „4”, „85%”), vagy
 *  `null`, ha a jegynek nincs strukturált értéke (szöveges, vagy évközi jegy).
 */
export function strukturaltRovid(item: any): string | null {
  if (!osszegzoE(item)) return null;
  const n = item?.ertek_szam;
  if (ures(n)) return null;
  if (item?.ertek_forma === 'jegy') return `${n}${Number(item?.dicseretes) === 1 && Number(n) === 5 ? 'D' : ''}`;
  if (item?.ertek_forma === 'szazalek') return `${n}%`;
  return null;
}

/** A jegy formája – a rögzített, vagy régi soroknál az értékből kikövetkeztetve. */
export function jegyFormaja(item: any): string {
  if (!ures(item?.ertek_forma)) return String(item.ertek_forma);
  const e = String(item?.ertekeles ?? item?.jegy ?? '').trim();
  if (osszegzoE(item)) return 'szoveges';
  if (e.endsWith('%')) return 'szazalek';
  if (/^[1-5]$/.test(e)) return 'erdemjegy';
  if (e && e.split('/').every((r) => FOKOZATOK.includes(r.trim()))) return 'fokozat';
  return 'szoveges';
}

/** Az új érték ellenőrzése – hibaüzenet, vagy `null`, ha rendben van. */
export function ertekHiba(forma: string, zaro: boolean, ertek: any, szoveg: any): string | null {
  const e = String(ertek ?? '').trim().toUpperCase().replace(/\s+/g, '');
  if (!zaro && (forma === 'erdemjegy' || forma === 'szazalek') && nemIrtaJelE(ertek)) return null;
  if (forma === 'erdemjegy' || forma === 'jegy') {
    const m = e.match(/^([1-5])(D?)$/);
    if (!m) return zaro ? 'Az érdemjegy 1–5 lehet, dicséretes ötösnél 5D.' : 'Az osztályzat 1–5 lehet (ha nem írta meg: –).';
    if (m[2] && (!zaro || m[1] !== '5')) return 'Dicséretes (5D) csak ötös záró- vagy negyedéves jegy lehet.';
    return null;
  }
  if (forma === 'szazalek') {
    const n = Number(e.replace('%', '').replace(',', '.'));
    if (!e || !Number.isFinite(n) || n < 0 || n > 100) {
      return zaro ? 'A százalék 0 és 100 közötti szám lehet.' : 'A százalék 0 és 100 közötti szám lehet (ha nem írta meg: –).';
    }
    return null;
  }
  if (forma === 'fokozat') {
    const reszek = String(ertek ?? '').split('/').map((r) => r.trim()).filter(Boolean);
    const jo = reszek.length > 0 && reszek.length <= 2
      && reszek.every((r) => FOKOZATOK.some((f) => f.toLowerCase() === r.toLowerCase()));
    return jo ? null : 'A fokozat Példás, Jó, Változó vagy Hanyag lehet (magatartás/szorgalomnál kettő, / jellel).';
  }
  return String(szoveg ?? ertek ?? '').trim() ? null : 'A szöveges értékelés nem lehet üres.';
}

export interface JegyModositasAdat {
  id: string;
  ertek: string;
  szoveg: string;
  targynap: string | null;
  tema: string | null;
}

const FORMA_NEV: Record<string, string> = {
  erdemjegy: 'osztályzat (1–5)', jegy: 'érdemjegy (1–5, dicséretes: 5D)', szazalek: 'százalék',
  fokozat: 'fokozat', szoveges: 'szöveges',
};

/**
 *  Bekéri az új értéket egy ablakban. `null`, ha a tanár megszakította.
 *  Hibás értéknél az ablak nyitva marad, és kiírja, mi a gond.
 */
export async function jegyModositasBekeres(alertCtrl: AlertController, item: any): Promise<JegyModositasAdat | null> {
  const zaro = osszegzoE(item);
  const forma = jegyFormaja(item);
  const eredeti = String(item?.ertekeles ?? item?.jegy ?? '').trim();
  const rovid = strukturaltRovid(item);
  //  Szöveg nélküli záró jegynél a szöveg maga a rövid alak – azt nem kínáljuk szövegnek.
  const szovegAlap = zaro && rovid && eredeti === rovid ? '' : eredeti;
  const datum = String(item?.targynap ?? item?.datum ?? '').slice(0, 10) || null;

  //  A „nem írta meg” (–) helyére a jegyet írjuk – a mező üresen indul. Évközi
  //  dolgozatnál szöveges a mező, hogy a mínusz (vissza) is beírható legyen.
  const nemIrta = nemIrtaE(item);
  const inputs: any[] = [];
  if (forma === 'erdemjegy') {
    inputs.push({ name: 'ertek', type: 'text', value: nemIrta ? '' : (item?.ertek_szam ?? eredeti),
      placeholder: '1–5 (ha nem írta meg: –)', attributes: { maxlength: 2, autocomplete: 'off' } });
  } else if (forma === 'jegy') {
    inputs.push({ name: 'ertek', type: 'text', value: rovid ?? '', placeholder: '1–5, dicséretes ötös: 5D' });
  } else if (forma === 'szazalek' && !zaro) {
    inputs.push({ name: 'ertek', type: 'text', value: nemIrta ? '' : (item?.ertek_szam ?? eredeti.replace('%', '')),
      placeholder: '0–100 (ha nem írta meg: –)', attributes: { maxlength: 4, autocomplete: 'off' } });
  } else if (forma === 'szazalek') {
    inputs.push({ name: 'ertek', type: 'number', min: 0, max: 100, value: item?.ertek_szam ?? eredeti.replace('%', ''), placeholder: '0–100' });
  } else if (forma === 'fokozat') {
    inputs.push({ name: 'ertek', type: 'text', value: eredeti, placeholder: 'Példás / Jó / Változó / Hanyag' });
  }
  if (forma === 'szoveges' || (zaro && (forma === 'jegy' || forma === 'szazalek'))) {
    inputs.push({ name: 'szoveg', type: 'textarea', value: forma === 'szoveges' ? eredeti : szovegAlap,
      placeholder: forma === 'szoveges' ? 'Szöveges értékelés' : 'Szöveges értékelés (nem kötelező)' });
  }
  inputs.push({ name: 'targynap', type: 'date', value: datum ?? '' });
  if (!zaro) inputs.push({ name: 'tema', type: 'text', value: item?.tema ?? '', placeholder: 'Téma' });

  let eredmeny: JegyModositasAdat | null = null;
  const alert = await alertCtrl.create({
    header: nemIrta ? 'Jegy beírása a kihúzás helyére' : 'Jegy módosítása',
    subHeader: `${zaro ? (NEGYEDEV_NEV[modjaKod(item)] || (modjaKod(item) === '3' ? 'Év végi értékelés' : 'Félévi értékelés')) : (nemIrta ? 'Nem írta meg (–)' : 'Évközi jegy')} · ${FORMA_NEV[forma] || forma}`,
    message: nemIrta ? 'Ha a tanuló pótolta a dolgozatot, írd be a jegyét. A dátum a dolgozaté marad, ha nem változtatod.' : undefined,
    inputs,
    buttons: [
      { text: 'Mégse', role: 'cancel' },
      {
        text: 'Mentés',
        handler: (adat: any) => {
          const hiba = ertekHiba(forma, zaro, adat?.ertek, adat?.szoveg);
          if (hiba) { alert.message = hiba; return false; }
          eredmeny = {
            id: String(item?.id ?? ''),
            ertek: String(adat?.ertek ?? '').trim(),
            szoveg: String(adat?.szoveg ?? '').trim(),
            targynap: String(adat?.targynap ?? '').slice(0, 10) || datum,
            tema: zaro ? (item?.tema ?? null) : (String(adat?.tema ?? '').trim() || null),
          };
          return true;
        },
      },
    ],
  });
  await alert.present();
  await alert.onDidDismiss();
  return eredmeny;
}

/*  A SAJÁTJÁT MINDENKI, A MÁSÉT SENKI.
 *
 *  A jegyet és a feljegyzést csak a beíró pedagógus (vagy adminisztrátor) módosíthatja
 *  és törölheti – a kiszolgáló is ezt mondja ki. A felületen ezért a kollégája tételénél
 *  meg sem jelennek a gombok: ne kattintson bele egy elutasításba.
 */
export function sajatTetelE(item: any, enyem: string[], admin = false): boolean {
  if (admin) return true;
  const kulcsok = new Set((enyem || []).map((x) => String(x ?? '').trim().toLowerCase()).filter(Boolean));
  if (!kulcsok.size) return true;               // ismeretlen kilét: a régi viselkedés
  return [item?.tanar, item?.konyvelte, item?.rogzitette]
    .map((x) => String(x ?? '').trim().toLowerCase()).filter(Boolean)
    .some((x) => kulcsok.has(x));
}

/*  STORNO – TÖRLÉS HELYETT (user, 2026-10-15).
 *
 *  Az Értékelések menü tételeit (jegy, záró- és negyedéves értékelés, feljegyzés, tervezett
 *  számonkérés) nem töröljük: a tétel megmarad „stornózva” jelöléssel, áthúzva látszik, és
 *  SEHOL nem számít – se átlagba, se a forma-zárolásba, se a záróértékelés előtöltésébe.
 *  A jegynél a jelölő a `stornozva`, a feljegyzésnél és a számonkérésnél a régi `torolve`.
 *  A kiszolgáló számításai ugyanígy kiszűrik (backend/ertekeles-naplo.js). */
export const stornoE = (item: any): boolean => Number(item?.stornozva) === 1 || Number(item?.torolve) === 1;

/** A napló műveleteinek felirata az előzmény-ablakban. */
export const NAPLO_MUVELET: Record<string, string> = {
  letrehozas: 'Beírás', modositas: 'Módosítás', storno: 'Stornó', torles: 'Törlés',
};

/**
 *  A storno okának bekérése. Az ok KÖTELEZŐ: a stornózott tétel áthúzva megmarad, és aki
 *  később ránéz (kolléga, admin), annak tudnia kell, miért került ki. `null`, ha mégsem.
 */
export async function stornoOkBekeres(alertCtrl: AlertController, mit: string, leiras?: string): Promise<string | null> {
  let ok: string | null = null;
  const alap = leiras
    || `A(z) ${mit} nem törlődik: áthúzva megmarad, és sehol nem számít (átlag, bizonyítvány, KRÉTA). Add meg a stornó okát.`;
  const alert = await alertCtrl.create({
    header: `${mit.charAt(0).toUpperCase()}${mit.slice(1)} stornózása`,
    message: alap,
    inputs: [{
      name: 'ok', type: 'textarea', placeholder: 'A stornó oka (pl. téves beírás, rossz tanuló)',
      attributes: { maxlength: 500 },
    }],
    buttons: [
      { text: 'Mégse', role: 'cancel' },
      {
        text: 'Stornózás',
        role: 'destructive',
        handler: (adat: any) => {
          const szoveg = String(adat?.ok ?? '').trim();
          if (!szoveg) { alert.message = 'A stornó okának megadása kötelező.'; return false; }
          ok = szoveg;
          return true;
        },
      },
    ],
  });
  await alert.present();
  await alert.onDidDismiss();
  return ok;
}
